/**
 * /sales/leads — sales-rep workspace.
 *
 * Two columns layout:
 *   Left:  list of claimable + my-assigned leads. Click → opens detail.
 *   Right: detail panel with claim / release / close actions.
 *
 * Lists refresh every 5s so reps see new claimable leads in near-real-time
 * (until WS push lands in A4).
 */

import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  type ConversationDetail,
  type ConversationMessage,
  type GroupSummary,
  type LeadDetail,
  type LeadSummary,
  type LeadTriggersResponse,
  Routes,
} from '@tg-leads/contracts';

import {
  LeadFilterChips,
  readSelectionFromSearch,
  writeSelectionToSearch,
} from '../../components/LeadFilterChips';
import { api, ApiError } from '../../lib/api';
import { friendlyError } from '../../lib/friendlyError';
import { toastError, toastInfo, toastSuccess } from '../../lib/toast';
import { useCurrentUser } from '../../lib/useCurrentUser';
import { useLeadRealtime } from '../../lib/useLeadRealtime';

interface LeadList {
  items: LeadSummary[];
  nextCursor: string | null;
}
interface GroupList {
  items: GroupSummary[];
  nextCursor: string | null;
}

const statusBadge = (s: LeadSummary['status']) => {
  switch (s) {
    case 'detected':
      return 'badge-warn';
    case 'assigned':
    case 'engaging':
    case 'replied':
      return 'badge-ok';
    case 'closed_won':
      return 'badge-ok';
    case 'closed_lost':
      return 'badge-fail';
    default:
      return 'badge-neutral';
  }
};

const statusLabel = (s: LeadSummary['status']): string => {
  switch (s) {
    case 'detected':
      return '待认领';
    case 'queued':
      return '待自动外联';
    case 'contacted':
      return '已发送等回复';
    case 'replied':
      return '对方已回复';
    case 'assigned':
      return '跟进中';
    case 'engaging':
      return '沟通中';
    case 'closed_won':
      return '已成单';
    case 'closed_lost':
      return '未成单';
    default:
      return s;
  }
};

export function SalesLeadsPage() {
  const me = useCurrentUser();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // WebSocket subscription — invalidates leads cache + shows toast on push.
  // Falls back to the 5s poll below if WS is down.
  useLeadRealtime({
    onNewLead: (lead) => {
      toastInfo(`有新线索：${lead.targetUserId}`, {
        description: '到左侧"可认领"列表查看',
      });
    },
    onReply: (msg) => {
      toastSuccess('对方回复了一条消息', {
        description: msg.text.slice(0, 80),
      });
    },
  });

  const [search, setSearch] = useSearchParams();
  const selection = useMemo(() => readSelectionFromSearch(search), [search]);

  const leadsUrl = useMemo(() => {
    const u = new URL(Routes.sales.leads, window.location.origin);
    if (selection.categories.length > 0)
      u.searchParams.set('categories', selection.categories.join(','));
    if (selection.tags.length > 0)
      u.searchParams.set('tags', selection.tags.join(','));
    return u.pathname + (u.search ? u.search : '');
  }, [selection]);

  const leads = useQuery({
    queryKey: ['sales', 'leads', selection.categories, selection.tags],
    queryFn: () => api.get<LeadList>(leadsUrl),
    refetchInterval: 5_000,
  });
  const groups = useQuery({
    queryKey: ['groups'],
    queryFn: () => api.get<GroupList>(Routes.admin.groups).catch(() => ({ items: [], nextCursor: null })),
  });

  const groupTitle = useMemo(() => {
    const map = new Map<string, string>();
    (groups.data?.items ?? []).forEach((g) => map.set(g.id, g.title));
    return (id: string) => map.get(id) ?? id.slice(0, 8);
  }, [groups.data]);

  const grouped = useMemo(() => {
    const items = leads.data?.items ?? [];
    return {
      mine: items.filter((l) => l.assignedSalesId === me.data?.id),
      claimable: items.filter(
        (l) => l.assignedSalesId === null && l.status === 'detected',
      ),
      other: items.filter(
        (l) => l.assignedSalesId !== null && l.assignedSalesId !== me.data?.id,
      ),
    };
  }, [leads.data, me.data]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">我的线索</h1>
        <p className="text-sm text-slate-500 mt-1">
          点"认领"把待认领线索接到自己名下；联系完成后用"成单 / 未成单"结案。
          页面会通过 WebSocket 实时推送，并兜底每 5 秒轮询一次。
        </p>
      </div>

      <LeadFilterChips
        statsUrl={Routes.sales.leadTagStats}
        selection={selection}
        onChange={(next) => setSearch(writeSelectionToSearch(search, next))}
      />

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-7 space-y-6">
          <LeadColumn
            title={`我在跟进 (${grouped.mine.length})`}
            items={grouped.mine}
            selectedId={selectedId}
            onSelect={setSelectedId}
            groupTitle={groupTitle}
            emptyHint="还没认领任何线索 — 从下方『可认领』列表里挑一条"
          />
          <LeadColumn
            title={`可认领 (${grouped.claimable.length})`}
            items={grouped.claimable}
            selectedId={selectedId}
            onSelect={setSelectedId}
            groupTitle={groupTitle}
            emptyHint="暂时没有可认领的线索，等管线识别出新的"
          />
        </div>
        <div className="col-span-5">
          {selectedId ? (
            <LeadDetailPanel
              id={selectedId}
              myId={me.data?.id ?? ''}
              onClose={() => setSelectedId(null)}
              groupTitle={groupTitle}
            />
          ) : (
            <div className="card text-sm text-slate-500">
              ← 在左侧选一条线索，查看详情和操作
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LeadColumn({
  title,
  items,
  selectedId,
  onSelect,
  groupTitle,
  emptyHint,
}: {
  title: string;
  items: LeadSummary[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  groupTitle: (id: string) => string;
  emptyHint: string;
}) {
  return (
    <div className="card overflow-hidden">
      <h2 className="text-sm font-semibold text-slate-700 mb-3">{title}</h2>
      {items.length === 0 ? (
        <div className="text-xs text-slate-500">{emptyHint}</div>
      ) : (
        <ul className="divide-y -mx-4">
          {items.map((l) => (
            <li
              key={l.id}
              onClick={() => onSelect(l.id)}
              className={`px-4 py-2.5 cursor-pointer hover:bg-slate-50 ${
                selectedId === l.id ? 'bg-indigo-50' : ''
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-slate-500 truncate">
                    {groupTitle(l.sourceGroupId)}
                  </div>
                  <div className="text-sm font-mono mt-0.5">{l.targetUserId}</div>
                  {(l.category || l.tags.length > 0) && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {l.category && (
                        <span className="text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">
                          {l.category}
                        </span>
                      )}
                      {l.tags.slice(0, 4).map((t) => (
                        <span
                          key={t}
                          className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded"
                        >
                          {t}
                        </span>
                      ))}
                      {l.tags.length > 4 && (
                        <span className="text-[10px] text-slate-400">
                          +{l.tags.length - 4}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <span className={statusBadge(l.status)}>{l.status}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function LeadDetailPanel({
  id,
  myId,
  onClose,
  groupTitle,
}: {
  id: string;
  myId: string;
  onClose: () => void;
  groupTitle: (id: string) => string;
}) {
  const qc = useQueryClient();
  const [actionErr, setActionErr] = useState<string | null>(null);

  const lead = useQuery({
    queryKey: ['sales', 'lead', id],
    queryFn: () => api.get<LeadDetail>(Routes.sales.lead(id)),
    refetchInterval: 5_000,
  });
  const triggers = useQuery({
    queryKey: ['sales', 'lead', id, 'triggers'],
    queryFn: () => api.get<LeadTriggersResponse>(Routes.sales.leadTriggers(id)),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['sales', 'leads'] });
    qc.invalidateQueries({ queryKey: ['sales', 'lead', id] });
  };

  const claimMut = useMutation({
    mutationFn: () =>
      api.post(Routes.sales.leadClaim(id), {
        expectedVersion: lead.data!.version,
      }),
    onSuccess: () => {
      invalidate();
      toastSuccess('已认领');
    },
    onError: (e: unknown) => {
      setActionErr(toMsg(e));
      toastError(e);
    },
  });
  const releaseMut = useMutation({
    mutationFn: () =>
      api.post(Routes.sales.leadRelease(id), {
        expectedVersion: lead.data!.version,
      }),
    onSuccess: () => {
      invalidate();
      toastInfo('已释放回抢单池');
    },
    onError: (e: unknown) => {
      setActionErr(toMsg(e));
      toastError(e);
    },
  });
  const closeMut = useMutation({
    mutationFn: (outcome: 'closed_won' | 'closed_lost') =>
      api.post(Routes.sales.leadClose(id), {
        expectedVersion: lead.data!.version,
        outcome,
      }),
    onSuccess: (_d, outcome) => {
      invalidate();
      toastSuccess(outcome === 'closed_won' ? '已标记成单 🎉' : '已标记未成单');
    },
    onError: (e: unknown) => {
      setActionErr(toMsg(e));
      toastError(e);
    },
  });

  if (lead.isLoading) {
    return <div className="card text-sm text-slate-500">加载中…</div>;
  }
  if (!lead.data) {
    return (
      <div className="card text-sm text-red-600">
        加载失败 {lead.error ? `(${(lead.error as Error).message})` : ''}
      </div>
    );
  }

  const d = lead.data;
  const isMine = d.assignedSalesId === myId;
  const isClaimable = d.assignedSalesId === null && d.status === 'detected';
  const canClose = isMine && ['assigned', 'engaging'].includes(d.status);

  return (
    <div className="card space-y-4">
      <div className="flex justify-between items-start">
        <div>
          <div className="text-xs text-slate-500">{groupTitle(d.sourceGroupId)}</div>
          <div className="text-lg font-mono mt-1">{d.targetUserId}</div>
        </div>
        <button
          className="text-slate-400 hover:text-slate-700 text-xl leading-none"
          onClick={onClose}
        >
          ×
        </button>
      </div>

      <div className="flex flex-wrap gap-2 text-xs">
        <span className={statusBadge(d.status)}>{statusLabel(d.status)}</span>
        <span className="badge-neutral">版本 v{d.version}</span>
        {isMine && <span className="badge-ok">我的</span>}
        {d.category && (
          <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">
            类目: {d.category}
          </span>
        )}
        {d.tags.map((t) => (
          <span
            key={t}
            className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded"
          >
            #{t}
          </span>
        ))}
      </div>

      <ConversationPanel leadId={id} myId={myId} canSend={isMine && d.status === 'assigned'} />

      <section className="border rounded-lg p-3 space-y-2">
        <h3 className="text-xs uppercase text-slate-500">来源消息（识别出该线索的群消息）</h3>
        {triggers.isLoading ? (
          <div className="text-xs text-slate-500">加载中…</div>
        ) : !triggers.data?.items.length ? (
          <div className="text-xs text-slate-500">触发消息已被定期清理，看不到原文</div>
        ) : (
          <ul className="space-y-2">
            {triggers.data.items.map((m) => (
              <li
                key={m.id}
                className="border-l-2 border-indigo-300 pl-2 py-0.5"
              >
                <div className="text-[10px] text-slate-500 flex gap-2 mb-0.5">
                  <span className="font-mono">{senderLabel(m)}</span>
                  <span>{new Date(m.ts).toLocaleString()}</span>
                </div>
                <div className="text-xs whitespace-pre-wrap break-words">
                  {m.text}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="border rounded-lg p-3">
        <h3 className="text-xs uppercase text-slate-500 mb-2">模型判定结果（原始 JSON）</h3>
        <pre className="text-xs whitespace-pre-wrap break-words bg-slate-50 p-2 rounded">
          {JSON.stringify(d.llmJudgmentJson, null, 2)}
        </pre>
      </section>

      <section className="border rounded-lg p-3 text-xs space-y-1">
        <div>
          <span className="text-slate-500">识别于</span>{' '}
          {new Date(d.createdAt).toLocaleString()}
        </div>
        {d.assignedAt && (
          <div>
            <span className="text-slate-500">认领于</span>{' '}
            {new Date(d.assignedAt).toLocaleString()}
          </div>
        )}
      </section>

      <div className="flex flex-wrap gap-2">
        {isClaimable && (
          <button
            className="btn-primary"
            disabled={claimMut.isPending}
            onClick={() => {
              setActionErr(null);
              claimMut.mutate();
            }}
          >
            {claimMut.isPending ? '认领中…' : '认领该线索'}
          </button>
        )}
        {isMine && d.status === 'assigned' && (
          <button
            className="btn-secondary"
            disabled={releaseMut.isPending}
            onClick={() => {
              setActionErr(null);
              releaseMut.mutate();
            }}
          >
            {releaseMut.isPending ? '释放中…' : '释放回抢单池'}
          </button>
        )}
        {canClose && (
          <>
            <button
              className="btn-primary"
              disabled={closeMut.isPending}
              onClick={() => {
                setActionErr(null);
                closeMut.mutate('closed_won');
              }}
            >
              标记成单
            </button>
            <button
              className="btn-danger"
              disabled={closeMut.isPending}
              onClick={() => {
                setActionErr(null);
                closeMut.mutate('closed_lost');
              }}
            >
              标记未成单
            </button>
          </>
        )}
        {d.status === 'closed_won' || d.status === 'closed_lost' ? (
          <div className="text-xs text-slate-500">已结案 — 无可操作</div>
        ) : null}
      </div>

      {actionErr && (
        <div className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded">
          {actionErr}
        </div>
      )}
    </div>
  );
}

function toMsg(e: unknown): string {
  if (e instanceof ApiError) return friendlyError(e);
  return String(e);
}

/** "@username · 张三" / "@username" / "张三 (id 999001)" / "id 999001" */
function senderLabel(m: {
  senderUserId: string;
  senderUsername?: string | null;
  senderFirstName?: string | null;
}): string {
  const parts: string[] = [];
  if (m.senderUsername) parts.push(`@${m.senderUsername}`);
  if (m.senderFirstName) parts.push(m.senderFirstName);
  if (parts.length === 0) return `id ${m.senderUserId}`;
  return `${parts.join(' · ')} (id ${m.senderUserId})`;
}

/**
 * Conversation panel — shown inside the lead detail. Renders the existing
 * message history (top: oldest, bottom: newest) and, if the lead is claimed
 * by `myId`, a reply input.
 *
 * Polls every 5s as a safety net; WebSocket `lead:reply` invalidates the
 * cache for near-instant updates when the prospect replies.
 */
function ConversationPanel({
  leadId,
  myId,
  canSend,
}: {
  leadId: string;
  myId: string;
  canSend: boolean;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const [sendErr, setSendErr] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const conv = useQuery({
    queryKey: ['sales', 'lead', leadId, 'conversation'],
    queryFn: () =>
      api.get<{ conversation: ConversationDetail | null }>(
        Routes.sales.leadConversation(leadId),
      ),
    refetchInterval: 5_000,
  });

  const reply = useMutation({
    mutationFn: (text: string) =>
      api.post<{ message: ConversationMessage }>(
        Routes.sales.leadConversationReply(leadId),
        { text },
      ),
    onSuccess: () => {
      setDraft('');
      setSendErr(null);
      qc.invalidateQueries({
        queryKey: ['sales', 'lead', leadId, 'conversation'],
      });
    },
    onError: (e: unknown) => {
      setSendErr(e instanceof ApiError ? friendlyError(e) : String(e));
    },
  });

  // Auto-scroll to bottom when new messages arrive.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [conv.data]);

  const data = conv.data?.conversation;

  return (
    <section className="border rounded-lg p-3 space-y-2 bg-slate-50">
      <div className="flex items-center justify-between">
        <h3 className="text-xs uppercase text-slate-500">私信会话</h3>
        {data && (
          <span className="text-[10px] text-slate-500 font-mono">
            发送账号 {data.accountLabel ?? data.accountId.slice(0, 8)}
          </span>
        )}
      </div>

      {conv.isLoading ? (
        <div className="text-xs text-slate-500">加载中…</div>
      ) : !data ? (
        <div className="text-xs text-slate-500 bg-white border border-dashed border-slate-300 rounded p-3">
          还没有私信会话。系统会在自动外联触发后由外联号发开场白，
          或者你认领后由你接手发送。
        </div>
      ) : (
        <>
          <div
            ref={scrollRef}
            className="bg-white border border-slate-200 rounded p-2 max-h-72 overflow-y-auto space-y-1.5"
          >
            {data.messages.length === 0 ? (
              <div className="text-xs text-slate-400 text-center py-4">
                会话已创建但还没消息（开场白正在发送）
              </div>
            ) : (
              data.messages.map((m) => (
                <MessageBubble key={m.id} m={m} myId={myId} />
              ))
            )}
          </div>
          {data.status === 'closed' && (
            <div className="text-[10px] text-slate-500">
              会话已关闭（线索已成单 / 未成单，或被系统回收）
            </div>
          )}
        </>
      )}

      {canSend && data?.status === 'open' && (
        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (!draft.trim()) return;
            reply.mutate(draft.trim());
          }}
          className="space-y-1"
        >
          <textarea
            className="input text-xs resize-none"
            rows={2}
            placeholder="输入要回的私信内容…（回车换行；点右侧按钮发送）"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className="flex justify-between items-center gap-2">
            <div className="text-[10px] text-slate-400">
              {draft.length}/4096 · 发送即调用 TG，发出后不能撤回
            </div>
            <button
              type="submit"
              className="btn-primary text-xs"
              disabled={!draft.trim() || reply.isPending}
            >
              {reply.isPending ? '发送中…' : '发送'}
            </button>
          </div>
          {sendErr && (
            <div className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
              {sendErr}
            </div>
          )}
        </form>
      )}
      {!canSend && data && data.status === 'open' && (
        <div className="text-[10px] text-slate-500">
          认领后才能发回复（且线索状态必须是"跟进中"）
        </div>
      )}
    </section>
  );
}

function MessageBubble({
  m,
  myId,
}: {
  m: ConversationMessage;
  myId: string;
}) {
  const isOut = m.direction === 'out';
  const author =
    m.sentBy === 'system' ? '系统（自动）' : m.sentBy === myId ? '你' : m.sentBy;
  return (
    <div
      className={`flex flex-col ${
        isOut ? 'items-end' : 'items-start'
      }`}
    >
      <div
        className={`max-w-[85%] rounded px-2 py-1 text-xs whitespace-pre-wrap break-words ${
          isOut
            ? 'bg-indigo-100 text-indigo-900 border border-indigo-200'
            : 'bg-slate-100 text-slate-800 border border-slate-200'
        }`}
      >
        {m.text}
      </div>
      <div className="text-[9px] text-slate-400 mt-0.5">
        {isOut ? author : '对方'} · {new Date(m.ts).toLocaleTimeString()}
      </div>
    </div>
  );
}
